import React, { useEffect } from "react";
import { Flex, Section, Table, Divider } from "@samskip/frontend-components";
import { useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector } from "@assets/hooks";
import { AddressType } from "@assets/constants";
import { getPartnerAddress, getPartnerRepresentatives } from "@state/slices/partnerSlice";

const CompanyView: React.FC<{ company: any }> = ({ company }) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const partnerAddress = useAppSelector((state) => state.partnerSlice.partnerAddress);
  const representatives = useAppSelector((state) => state.partnerSlice.representatives);

  useEffect(() => {
    if (company?.PartnerCode) {
      dispatch(getPartnerAddress({
        partnerCode: company.PartnerCode,
        addressType: AddressType.DEFAULT,
      }));
      dispatch(getPartnerRepresentatives(company.PartnerCode));
    }
  }, [company?.PartnerCode, dispatch]);

  const companyRow1Headers = [
    { id: "fullName", name: t("LABEL_NAME"), className: "w-1/3" },
    { id: "compRegNo", name: t("LABEL_SSN"), className: "w-1/3" },
    { id: "country", name: t("LABEL_COUNTRY"), className: "w-1/3" },
  ];

  const companyRow1Cells = [
    company?.FullName,
    company?.CompanyRegNo || "",
    company?.Country,
  ];

  const companyRow2Headers = [
    { id: "address", name: t("LABEL_ADDRESS"), className: "w-1/3" },
    { id: "city", name: t("LABEL_CITY"), className: "w-1/3" },
    { id: "postalCode", name: t("LABEL_POSTALCODE"), className: "w-1/3" },
  ];

  const companyRow2Cells = [
    company?.Address2,
    company?.Address3,
    company?.PostCode || "",
  ];

  const addressHeaders = [
    { id: "addressType", name: t("LABEL_ADDRESS_TYPE"), className: "w-1/6" },
    { id: "name", name: t("LABEL_NAME"), className: "w-1/6" },
    { id: "address", name: t("LABEL_ADDRESS"), className: "w-1/6" },
    { id: "zip", name: t("LABEL_POSTALCODE"), className: "w-1/6" },
    { id: "location", name: t("LABEL_LOCATION"), className: "w-1/6" },
    { id: "country", name: t("LABEL_COUNTRY"), className: "w-1/6" },
  ];

  const contactHeaders = [
    { id: "transport", name: t("LABEL_IMP_EXP"), className: "w-1/3" },
    { id: "department", name: t("LABEL_DEPARTMENT"), className: "w-1/3" },
    { id: "employee", name: t("LABEL_EMPLOYEE"), className: "w-1/3" },
  ];

  return (
    <Flex gap="large" vertical>
      <Section title={t("LABEL_COMPANY")}>
        <Flex gap="medium" vertical>
          <Table id="company-info-1">
            <Table.Header>
              <Table.HeaderRow>
                {companyRow1Headers.map((h) => (
                  <Table.HeaderCell className={h.className} id={h.id} key={h.id}>
                    {h.name}
                  </Table.HeaderCell>
                ))}
              </Table.HeaderRow>
            </Table.Header>
            <Table.Body>
              <Table.Row id="row1">
                {companyRow1Cells.map((cell, i) => (
                  <Table.Cell
                    className={companyRow1Headers[i].className}
                    id={companyRow1Headers[i].id}
                    key={companyRow1Headers[i].id}
                  >
                    {cell || ""}
                  </Table.Cell>
                ))}
              </Table.Row>
            </Table.Body>
          </Table>

          <Table id="company-info-2">
            <Table.Header>
              <Table.HeaderRow>
                {companyRow2Headers.map((h) => (
                  <Table.HeaderCell className={h.className} id={h.id} key={h.id}>
                    {h.name}
                  </Table.HeaderCell>
                ))}
              </Table.HeaderRow>
            </Table.Header>
            <Table.Body>
              <Table.Row id="row2">
                {companyRow2Cells.map((cell, i) => (
                  <Table.Cell
                    className={companyRow2Headers[i].className}
                    id={companyRow2Headers[i].id}
                    key={companyRow2Headers[i].id}
                  >
                    {cell || ""}
                  </Table.Cell>
                ))}
              </Table.Row>
            </Table.Body>
          </Table>
        </Flex>
      </Section>

      <Divider />

      <Section title={t("LABEL_COMPANY_ADDRESSES")}>
        {partnerAddress ? (
          <Table id="company-addresses">
            <Table.Header>
              <Table.HeaderRow>
                {addressHeaders.map((h) => (
                  <Table.HeaderCell className={h.className} id={h.id} key={h.id}>
                    {h.name}
                  </Table.HeaderCell>
                ))}
              </Table.HeaderRow>
            </Table.Header>
            <Table.Body>
              <Table.Row id={partnerAddress.AddressCode}>
                {[
                  partnerAddress.AddressTypeDescription,
                  partnerAddress.FullName,
                  partnerAddress.Address1,
                  partnerAddress.PostCode,
                  partnerAddress.City,
                  partnerAddress.CountryName,
                ].map((cell, index) => (
                  <Table.Cell
                    className={addressHeaders[index].className}
                    ellipsis="normal"
                    id={addressHeaders[index].id}
                    key={addressHeaders[index].id}
                  >
                    {cell || ""}
                  </Table.Cell>
                ))}
              </Table.Row>
            </Table.Body>
          </Table>
        ) : null}
      </Section>

      <Divider />

      <Section title={t("LABEL_REPRESENTATIVES")}>
        {representatives.length > 0 ? (
          <Table id="company-contacts">
            <Table.Header>
              <Table.HeaderRow>
                {contactHeaders.map((h) => (
                  <Table.HeaderCell className={h.className} id={h.id} key={h.id}>
                    {h.name}
                  </Table.HeaderCell>
                ))}
              </Table.HeaderRow>
            </Table.Header>
            <Table.Body>
              {representatives.map((rep, index) => (
                <Table.Row id={String(rep.CodeValue || index)} key={rep.CodeValue || index}>
                  {[rep.TransportEng, rep.DepartmentEng, rep.EmployeeName].map((cell, i) => (
                    <Table.Cell
                      className={contactHeaders[i].className}
                      ellipsis="normal"
                      id={contactHeaders[i].id}
                      key={contactHeaders[i].id}
                    >
                      {cell || ""}
                    </Table.Cell>
                  ))}
                </Table.Row>
              ))}
            </Table.Body>
          </Table>
        ) : null}
      </Section>
    </Flex>
  );
};

export default CompanyView;